import { api } from '@/lib/api/client';
import type { Query } from '@/lib/api/client';
import { invalidateStations } from '@/lib/api/airports';
import type { JournalResponse, ServerResponse } from '@/lib/api/types';
import type { Airport, AirportSortField } from '@/types/airport';

/**
 * The airport dictionary.
 *
 * Reading is open to `ADMIN`, `AAK`, `USER` and `KGA_USER`; **writing is ADMIN
 * only**, and a refusal there is a transport-level `403` rather than a
 * `successfull: false` body. The two are different answers and the screen keeps them
 * apart: one means "not permitted", the other means "that was not a valid airport".
 *
 * This is shared master data — the reference every schedule integration matches
 * flights against — so a write here is not a local edit.
 */

/** The filter, page and order for `/airport/list`. Blank values are dropped. */
export interface AirportCriteria {
  /** One box for "search anything": code, ICAO, airport / city / country name. */
  query?: string;
  /** A whole ISO code (`KZ`) or part of a country name in any language. */
  country?: string;
  city?: string;
  /** The WHOLE code. Partial matching is what `query` is for. */
  iataCode?: string;
  icaoCode?: string;
  active?: boolean;
  pageNum: number;
  rows: number;
  sortBy: AirportSortField;
  sortDirection: 'ascending' | 'descending';
}

/**
 * A page of the dictionary — the endpoint a table should be built on.
 *
 * A GET with query parameters, unlike the flight board's POST body. `buildQuery`
 * already drops blanks, so form state can be handed straight through: `?city=` is
 * not a filter and this endpoint ignores it.
 *
 * `sortBy` is validated against `AIRPORT_SORT_FIELDS` before it gets here, because
 * an unknown field is answered with a **400** naming the allowed ones rather than
 * being quietly ignored.
 */
export const fetchAirportPage = (
  criteria: AirportCriteria,
  signal?: AbortSignal,
): Promise<JournalResponse<Airport>> =>
  api.get<JournalResponse<Airport>>('/airport/list', toQuery(criteria), signal);

/**
 * The criteria as parameters, written out rather than cast.
 *
 * A cast would compile and would also let a field renamed on one side drift silently
 * past the other; naming each pair is the version of this that a rename breaks.
 */
const toQuery = (criteria: AirportCriteria): Query => ({
  query: criteria.query,
  country: criteria.country,
  city: criteria.city,
  iataCode: criteria.iataCode,
  icaoCode: criteria.icaoCode,
  active: criteria.active,
  pageNum: criteria.pageNum,
  rows: criteria.rows,
  sortBy: criteria.sortBy,
  sortDirection: criteria.sortDirection,
});

/**
 * One airport.
 *
 * **A missing id is `200 null`, not a 404** — the same trap as everywhere else in
 * this backend, so "the request worked" does not mean "the airport exists".
 */
export const fetchAirport = (id: number, signal?: AbortSignal): Promise<Airport | null> =>
  api.get<Airport | null>(`/airport/id/${id}`, undefined, signal);

/**
 * The outcome of a write, as a union so the check cannot be skipped.
 *
 * A rejected write — a duplicate IATA code, a blank required field, an id that does
 * not exist — arrives as **HTTP 200** with `successfull: false` (the backend's own
 * spelling). Branching on the status code reports every one of those as a success.
 */
export type AirportWriteResult =
  | { ok: true; airport: Airport }
  | { ok: false; message: string | null };

/** Delete answers with `payload: null` even on success, so it has no airport to carry. */
export type AirportDeleteResult = { ok: true } | { ok: false; message: string | null };

const settle = (response: ServerResponse<Airport>): AirportWriteResult => {
  if (response.successfull && response.payload !== null) {
    // The dictionary just moved, and the board's station list is a cached copy of
    // part of it in this same tab.
    invalidateStations();
    return { ok: true, airport: response.payload };
  }

  return { ok: false, message: response.errorMessage };
};

/**
 * Creates an airport.
 *
 * `id`, `created` and `updated` in the body are ignored; the code is upper-cased and
 * must not already exist. **`active` defaults to `false` when omitted**, which is
 * why the form always sends it rather than leaving it out when unticked.
 */
export const createAirport = (airport: Partial<Airport>): Promise<AirportWriteResult> =>
  api.post<ServerResponse<Airport>>('/airport/add', airport).then(settle);

/**
 * Updates an airport.
 *
 * **This is a REPLACE.** The server expects the whole object back with the changes
 * applied, so a column left out of the body is a column being cleared — including
 * one this app does not model. Callers build the body by spreading the row they
 * loaded; see `AirportPanel`.
 *
 * The path `{id}` is authoritative, so an `id` in the body is ignored.
 */
export const editAirport = (id: number, airport: Partial<Airport>): Promise<AirportWriteResult> =>
  api.post<ServerResponse<Airport>>(`/airport/edit/id/${id}`, airport).then(settle);

/**
 * Removes an airport. **The row is gone** — this is not a soft delete.
 *
 * Hiding an airport from selection is `editAirport` with `active: false`, and that
 * is what the screen offers first: a code that flights have been booked against
 * should stop being offered, not stop existing.
 */
export const deleteAirport = (id: number): Promise<AirportDeleteResult> =>
  api.post<ServerResponse<null>>(`/airport/delete/id/${id}`).then((response) => {
    if (response.successfull) {
      invalidateStations();
      return { ok: true };
    }

    return { ok: false, message: response.errorMessage };
  });
